<!--
  The app shell. Owned by the SHELL + DESIGN SYSTEM agent
  (web/src/lib/ui/ + web/src/routes/).

  FOUR STATES, IN ORDER OF AUTHORITY:
    1. the invite gate — until /api/session says this browser is allowed in;
    2. the watch screen — full bleed, its own chrome, no shell of ours at all;
    3. the phone shell  — logo + glass bubbles above, fixed tab bar below (<768px);
    4. the desktop/tablet shell — one three-zone top bar (≥768px).

  THE BROWSE CHROME NEVER TOUCHES THE WATCH SCREEN. `route.name === 'watch'` is
  handled before either shell exists, so the tab bar cannot end up over a film,
  under the transport bar, or inside a full-screen element — there is no code path
  where both are mounted.

  ONE CODEBASE, ONE SET OF ROUTES. The two shells differ in chrome and nothing
  else: every screen below renders identically under either.
-->
<script lang="ts">
  import SocialLayer from '$lib/social/SocialLayer.svelte';
  import Landing from './routes/Landing.svelte';
  import {guestCanBrowse}from '$lib/ui/guest-access';
  import InvitationLanding from '$lib/social/InvitationLanding.svelte';
  import { roomInvitation, captureRoomInvitation } from '$lib/social/invite-handoff';
  import BackToTop from '$lib/ui/BackToTop.svelte';
  import BubbleNav from '$lib/ui/BubbleNav.svelte';
  import Intro from '$lib/ui/Intro.svelte';
  import { trackKeyboardInset } from '$lib/ui/keyboard-inset';
  import OfflineBanner from '$lib/ui/OfflineBanner.svelte';
  import LivingGround from '$lib/ui/LivingGround.svelte';
  import { shouldPlayIntro } from '$lib/ui/intro';
  import TabBar from '$lib/ui/TabBar.svelte';
  import TopBar from '$lib/ui/TopBar.svelte';
  import { focusScreen, startDpad } from '$lib/ui/dpad';
  import { go, nav, startRouter } from '$lib/ui/nav.svelte';
  import { gate, openGate } from '$lib/ui/session.svelte';
  import { shell, watchShell } from '$lib/ui/shell.svelte';

  import Browse from './routes/Browse.svelte';
  import Categories from './routes/Categories.svelte';
  import Collection from './routes/Collection.svelte';
  import Friends from './routes/Friends.svelte';
  import Gate from './routes/Gate.svelte';
  import Home from './routes/Home.svelte';
  import MyJovi from './routes/MyJovi.svelte';
  import NotFound from './routes/NotFound.svelte';
  import Person from './routes/Person.svelte';
  import Profile from './routes/Profile.svelte';
  import Room from './routes/Room.svelte';
  import Search from './routes/Search.svelte';
  import Title from './routes/Title.svelte';
  import Watch from './routes/Watch.svelte';

  $effect(() => startRouter());
  /* Publishes --kb-inset for the dock. See keyboard-inset.ts. */
  $effect(() => trackKeyboardInset());

  $effect(() => {
    const invitationClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!(anchor instanceof HTMLAnchorElement) || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
      const url = new URL(anchor.href);
      if (url.origin !== location.origin || !new URLSearchParams(url.hash.slice(1)).has('roomInvite')) return;
      event.preventDefault();
      // The normal delegated router drops fragments, so capture this link first.
      roomInvitation.capture(url.href, path => history.replaceState(history.state, '', path));
    };
    document.addEventListener('click', invitationClick, true);
    window.addEventListener('hashchange', captureRoomInvitation);
    return () => {
      document.removeEventListener('click', invitationClick, true);
      window.removeEventListener('hashchange', captureRoomInvitation);
    };
  });
  $effect(() => watchShell());
  /*
    THE REMOTE. One `keydown` listener for the whole app, outside the gate
    because a television has to be able to reach the invite field too, and it
    does nothing at all until a directional key actually arrives — so a phone and
    a tablet never notice it exists. See `dpad.ts`.
  */
  $effect(() => startDpad());
  $effect(() => {
    void openGate();
  });

  const route = $derived(nav.route);

  /*
    /my IS /profile NOW.

    My Jovii's content — the saved shelf, the suggestions, continue watching —
    moved onto the Profile screen in the phone-UX pass, and the dock has four
    tabs rather than five. The ROUTE stays, because links to /my are in
    conversations and in browser histories and must not 404; it replaces itself
    with /profile on arrival.

    `replace: true` deliberately: /my must not sit in the history behind
    /profile, or Back from the profile would bounce through this redirect and
    land on the profile again — a Back button that does nothing.

    In an effect and not in `parse()`: the router's parser is a pure function
    over a URL (nav.test.ts asserts that) and making it navigate would give one
    function two jobs and no way to test either.
  */
  $effect(() => {
    if (nav.route.name === 'mine') go('/profile', { replace: true, keepScroll: true });
  });

  /*
    THE FIRST-RUN INTRO. Decided ONCE, synchronously, before the first paint —
    if this were reactive the animation could re-arm mid-session, and if it were
    read after mount the app would flash behind it. It draws over whatever the
    shell is doing, including the invite gate, so a cold start looks the same to
    a signed-out visitor as to an account holder. See `lib/ui/intro.ts` for
    which starts count as cold.
  */
  let intro = $state(shouldPlayIntro());

  /*
    A SCREEN CHANGED, SO THE REMOTE NEEDS SOMEWHERE TO BE.

    Pressing OK on a poster tears the shelf down and builds a title page, so the
    element that held the focus is gone and the focus falls back to `<body>`.
    With a mouse that is invisible and correct; with a remote it is a dead end.

    Keyed on the whole route and not on `route.name`, because /movie/tt1 and
    /movie/tt2 are the same name and a different screen. Does nothing at all
    unless a directional key has been pressed at some point in this session —
    a pointer and a thumb never reach `focusScreen`'s body. See `dpad.ts`.

    NOT THE WATCH SCREEN, and it is the same rule the rest of this file follows:
    the browse layer's furniture does not touch the player. A film's resting
    state is deliberately NOTHING FOCUSED — that is what lets Left and Right
    mean ten seconds there — and arriving with a ring on the transport bar would
    take the seek keys away until the chrome faded out from under it.
  */
  $effect(() => {
    void JSON.stringify(nav.route);
    if (nav.route.name !== 'watch') focusScreen();
  });
</script>

<!--
  ONE SOCIAL LAYER, FOR THE WHOLE APP.
  
  It hands the store the tab's room socket and draws the "can I come in?"
  prompt. It used to be mounted by Profile, Friends and Rooms — the three
  screens its author owned — which meant a friend knocking while you were
  WATCHING SOMETHING, the moment they are most likely to knock, reached nobody.
  Mounted once here it is on every screen including the watch screen, and there
  is exactly one of it, which matters: four copies were four subscriptions and
  four prompts for one question.

  Inside the gate, because a browser that has not been let in has no account and
  nothing to be asked about.
-->
<!-- The living ground: the theme's colours, drifting, behind everything. -->
<LivingGround />

{#if intro}
  <Intro done={() => (intro = false)} />
{/if}

{#if gate.state === 'checking'}
  <!-- One frame of nothing rather than a flash of the lock screen. -->
  <div class="boot" aria-busy="true"></div>
{:else if gate.state === 'offline' && gate.offlineWith === null}
  <!-- Never been let in, and no server to ask. The wall is honest here. -->
  <Gate />
{:else if gate.state === 'offline'}
  <!--
    OFFLINE MODE. The shell came from the cache and the server is not there,
    but this browser was let in before and its downloads are on the disk. So:
    the app, opened on Profile (where Downloads lives), with a banner that
    says what this is. Every screen that needs the catalogue will show its
    own empty state; nothing here pretends the network exists.
  -->
  {#if route.name === 'watch'}
    <!-- Play on a download. Full bleed like any watch screen; the player asks
         no link service when the file is on the disk (controller.autoSelect). -->
    {#key `${route.kind}:${route.imdbId}`}
      <Watch />
    {/key}
  {:else}
    <OfflineBanner />
    {#if shell.phone}
      <BubbleNav />
    {:else}
      <TopBar />
    {/if}
    <main class:phone={shell.phone}>
      <Profile />
    </main>
    {#if shell.phone}
      <TabBar />
    {/if}
  {/if}
{:else if !gate.accounts && gate.state !== 'open'}
  <Gate />
{:else if route.name === 'welcome'}
  <Landing />
{:else if gate.state !== 'open' && (!guestCanBrowse(route) || $roomInvitation.phase !== 'none')}
  <div class="guest-auth">
    <a href="/">← Keep browsing</a><a href="/welcome">About Jovii</a>
    {#if route.name === 'watch'}<p>Create an account or sign in to watch. Your film or episode will open after you sign in.</p>{/if}
    <Gate initialMode={route.name === 'watch' ? 'register' : 'login'} />
  </div>
{:else if $roomInvitation.phase !== 'none'}
  <InvitationLanding />
{:else if route.name === 'watch'}
  <SocialLayer />
  <!--
    Keyed on WHAT is being watched, and deliberately NOT on `route.roomId`.

    A room is not a change of film. With the room in the key, pressing "Watch
    together" changed the key, so Svelte destroyed the whole watch screen and
    built a new one: the film restarted from zero and re-probed all 53 links.
    Worse, the tab's sync engine is a singleton and SURVIVES that — so it went on
    to publish its existing clock into the brand-new room while the fresh
    controller was still probing, and the server rightly refused with "cannot
    publish a clock before setSource". The room then sat on "Waiting for
    everyone…" for ever.

    Keeping the component alive keeps the verified source with it, so joining a
    room pins it immediately and the film simply carries on.

    AND NOT ON `season`/`episode` EITHER, for exactly the same reason — the
    argument above was written and then only half applied. An episode is not a
    change of screen: `PlayerController.switchContent` handles it internally,
    precisely so the film does not restart. With the episode in this key it did
    restart, and worse: `switchContent` moves the address bar BEFORE the pin
    lands, so this key changed while the new episode's probe was still in flight,
    Svelte tore the screen down, `detach()` bumped `pickGeneration`, and the
    orphaned `commit()` never sent its `setSource`. That is a race between the
    probe and this screen's own churn — which the HOST loses too whenever the
    probe is slow (a longer candidate list, a cold link, a second browser
    competing for Real-Debrid), i.e. exactly when several people are watching.
  -->
  {#key `${route.kind}:${route.imdbId}`}
    <Watch />
  {/key}
{:else}
  {#if shell.phone}
    <BubbleNav />
  {:else}
    <TopBar />
  {/if}

  <main class:phone={shell.phone}>
    {#if route.name === 'home' || route.name === 'signin'}
      <Home />
    {:else if route.name === 'browse'}
      {#key `${route.kind}:${route.genre ?? ''}`}
        <Browse />
      {/key}
    {:else if route.name === 'collection'}
      <!-- Keyed like Browse: two collections are two screens, not one reused. -->
      {#key route.slug}
        <Collection />
      {/key}
    {:else if route.name === 'person'}
      <!-- Keyed like Collection: two people are two screens. -->
      {#key route.key}
        <Person />
      {/key}
    {:else if route.name === 'categories'}
      <Categories />
    {:else if route.name === 'mine'}
      <!-- One frame at most: the effect above replaces this with /profile. The
           full history screen this route used to draw lives on in MyJovi.svelte
           and is reachable from Profile's own history entry. -->
      <MyJovi />
    {:else if route.name === 'search'}
      <Search />
    {:else if route.name === 'title'}
      {#key `${route.kind}:${route.imdbId}`}
        <Title />
      {/key}
    {:else if route.name === 'room'}
      <Room />
    {:else if route.name === 'profile' || route.name === 'settings'}
      <!-- One destination. /settings is kept as an alias so older links still land. -->
      <Profile />
    {:else if route.name === 'friends'}
      <Friends />
    {:else}
      <NotFound />
    {/if}

    <!--
      THE WAY BACK UP, once, for every screen in the shell. Inside <main> so
      the watch screen — which never mounts this branch — can never grow one,
      and after the routed content so it is the literal bottom of every page.
      It renders nothing at all on a page that does not scroll; see its header.
    -->
    <BackToTop />
  </main>

  {#if shell.phone}
    <TabBar />
  {/if}

  {#if gate.state === 'open'}<SocialLayer />{/if}
{/if}

<style>
  .guest-auth > a {display:inline-flex;align-items:center;min-height:44px;margin:16px 24px;color:var(--text);}
  .guest-auth > p {margin:0 24px;color:var(--text-dim);max-width:65ch;}
  .guest-auth :global(.gate) {min-height:calc(100dvh - 110px);}
  .boot {
    min-height: 100dvh;
  }

  /*
   * EDGE TO EDGE, AT EVERY WIDTH.
   *
   * This used to cap at --page-max and centre, which put two empty columns down
   * the sides of a wide screen — dead ground either side of a catalogue whose
   * whole cue for "there is more" is a row running off the edge of the picture.
   * A rail that stops 200px short of the glass says the opposite.
   *
   * The cap does not disappear, it moves: --page-pad still holds text and
   * headings off the edge, and Hero pins its own copy to a readable measure.
   * What runs full width is the ARTWORK — rails, and the banner behind them.
   */
  main {
    display: block;
    width: 100%;
    padding-bottom: max(24px, var(--safe-b));
  }

  /*
   * THE TAB BAR MUST NOT COVER CONTENT.
   *
   * The bar is fixed, so it takes no space in the flow, and this is the single
   * place that pays for it: the height of the bar, plus the home-indicator inset
   * it sits above, plus a breath so the last row is not flush against it. One
   * owner of the number — the bar itself never sets a margin on anything.
   */
  main.phone {
    padding-bottom: calc(var(--tabbar-h) + var(--safe-b) + var(--tabbar-gap) + var(--space-5));
  }
</style>